import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { api, type Report } from '../lib/api';
import { fmtKstShort } from '../lib/time';
import { ErrorBox } from './Watchlist';

/**
 * 보고서 raw vision markdown viewer.
 * reports.extractedText (vision_extract v4 의 raw mode 출력) 표시.
 */
export default function ReportMdPage() {
  const { id } = useParams<{ id: string }>();
  const [meta, setMeta] = useState<Report | null>(null);
  const [md, setMd] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    // /:id 는 메타 (reports:read), /:id/text 는 sensitive markdown (reports:text scope).
    // codex P1: read 응답에 extractedText 노출하면 scope boundary 우회 → 분리 호출.
    Promise.all([api.report(id), api.reportText(id)])
      .then(([r, t]) => {
        setMeta(r);
        setMd(t.text);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [id]);

  if (error) return <ErrorBox msg={error} />;
  if (loading) return <p className="text-slate-500">로딩 중…</p>;
  if (!meta) return <p className="text-slate-500">보고서를 찾을 수 없습니다.</p>;

  return (
    <section>
      <div className="flex items-baseline justify-between">
        <div>
          <Link to="/reports" className="text-xs text-slate-500 hover:underline">
            ← Reports
          </Link>
          <h1 className="mt-1 text-xl font-semibold tracking-tight">{meta.title}</h1>
          <p className="mt-1 text-sm text-slate-600">
            <Link to={`/wikis/${meta.symbol}`} className="hover:underline">
              {meta.symbolName ?? meta.symbol}
            </Link>
            <span className="ml-1.5 font-mono text-[10px] text-slate-400">{meta.symbol}</span>
            <span className="mx-2 text-slate-300">·</span>
            {meta.broker ?? '—'}
            {meta.analyst ? <span className="ml-1 text-slate-500">({meta.analyst})</span> : null}
            <span className="mx-2 text-slate-300">·</span>
            <span className="text-slate-500">{fmtKstShort(meta.collectedAt)}</span>
          </p>
        </div>
        <a
          href={api.pdfUrl(meta.id)}
          target="_blank"
          rel="noreferrer"
          className="rounded-md border border-slate-200 px-3 py-1.5 text-sm hover:bg-slate-50"
        >
          📄 원본 PDF
        </a>
      </div>

      <div className="mt-2 text-xs text-slate-500">
        Raw vision markdown (v4 raw extraction mode){' '}
        {md != null ? <span>· {md.length.toLocaleString()} chars</span> : null}
      </div>

      <article className="prose prose-slate prose-sm mt-5 max-w-none">
        {md ? (
          <ReactMarkdown remarkPlugins={[[remarkGfm, { singleTilde: false }]]}>{md}</ReactMarkdown>
        ) : (
          <p className="text-slate-500">아직 raw markdown 이 추출되지 않았습니다.</p>
        )}
      </article>
    </section>
  );
}
